import { FilesIcon } from 'lucide-react'
import pluralize from 'pluralize'
import { useMemo } from 'react'

import { Tooltip } from '@/components/ui'
import { Tally, useStatements } from '@/modules/projects'
import { cn, humanizeText } from '@/utils'
import { Progress } from './Progress'
import {
  describeStatus,
  isEveryPageSucceeded,
  PAGE_STATUSES,
  type PageStatus,
  pageStatusDotClass,
  tallyPagesByStatus,
} from './pageStatuses'

const statusLabel = (status: PageStatus) => humanizeText(status).toLowerCase()

interface ProjectStateBarProps {
  className?: string
  alwaysVisible?: boolean
  fullWidth?: boolean
  compact?: boolean
}

export const ProjectStateBar = ({
  className,
  alwaysVisible = false,
  fullWidth = false,
  compact = false,
}: ProjectStateBarProps) => {
  const statements = useStatements()
  const tally = useMemo(() => tallyPagesByStatus(statements), [statements])
  const { statementCount, totalPages, statuses } = tally

  const isDone = isEveryPageSucceeded(tally)
  const showProgress = alwaysVisible || !isDone

  const legendStatuses = PAGE_STATUSES.filter((status) => statuses[status] > 0)

  return (
    <div
      className={cn(
        !compact && 'rounded-lg border bg-white p-6',
        !compact && !fullWidth && 'xl:max-w-max',
        className,
      )}
    >
      <div className="flex flex-col gap-2">
        {showProgress && (
          <div
            role="progressbar"
            aria-label="Pages processed"
            aria-valuemin={0}
            aria-valuemax={totalPages}
            aria-valuenow={statuses.succeeded}
            className="bg-muted flex h-2 overflow-hidden rounded-sm"
          >
            {PAGE_STATUSES.map((status) => (
              <Progress
                key={`progress-${status}`}
                value={statuses[status]}
                max={totalPages}
                status={status}
                title={`${humanizeText(status)}: ${describeStatus(tally, status)}`}
              />
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-sm">
          {!compact && (
            <div className="flex items-center gap-2">
              <FilesIcon className="size-4 text-muted-foreground" />
              <span className="font-semibold tabular-nums text-foreground">
                {totalPages.toLocaleString()}
              </span>
              <span className="text-muted-foreground">
                {pluralize('page', totalPages)} uploaded across{' '}
                <span className="font-medium tabular-nums text-foreground">
                  {statementCount.toLocaleString()}
                </span>{' '}
                {pluralize('statement', statementCount)}
              </span>
            </div>
          )}

          {legendStatuses.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              {legendStatuses.map((status) => (
                <Tooltip
                  key={`tally-${status}`}
                  content={describeStatus(tally, status)}
                >
                  <div>
                    <Tally
                      label={statusLabel(status)}
                      count={statuses[status]}
                      dotClassName={pageStatusDotClass(status)}
                    />
                  </div>
                </Tooltip>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
